import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Tab,
  TextField,
  CircularProgress,
  IconButton,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import Grid from "@mui/material/Grid";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";

import AppDialog from "../components/General/Dialog/AppDialog";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";
import GlassCard from "../components/General/GlassCard";
import ActionHeaderCard from "../components/General/ActionHeaderCard";
import DataGridCustom from "../components/General/DatagridCustom";
import IndividualDependencyItemTypesDialog from "../components/IndividualLiquidation/catalog/IndividualDependencyItemTypesDialog";
import CreateDependencyDialog from "../components/Liquidation/catalog/CreateDependencyDialog";
import DateAmountPanel from "../components/Liquidation/DateAmountPanel";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import {
  getIndividualItemTypes,
  createIndividualItemType,
  updateIndividualItemType,
  deleteIndividualItemType,
  getIndividualDependenciesConfig,
  createIndividualDependency,
  getJudicialInterestRates,
  createJudicialInterestRate,
  updateJudicialInterestRate,
  deleteJudicialInterestRate,
  importJudicialInterestRatesBcra,
  getIndividualBarAssocValues,
  createIndividualBarAssocValue,
  updateIndividualBarAssocValue,
  deleteIndividualBarAssocValue,
  getIndividualBarCollegeFees,
  createIndividualBarCollegeFee,
  updateIndividualBarCollegeFee,
  deleteIndividualBarCollegeFee,
} from "../services/IndividualLiquidationService";

function fmtDate(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("es-AR");
}

const CATEGORY_OPTIONS = ["capital", "honorarios", "gastos"];
const RATE_TYPE_LABELS = { pasiva: "Tasa Pasiva", tim: "TIM (moratorio)", banco_cordoba: "Banco de Córdoba" };

// ── Rubros ───────────────────────────────────────────────────────────────────

function ItemTypesPanel() {
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    name: "",
    key: "",
    category: "gastos",
    default_amount_mode: "fixed",
    allows_interest: true,
    applies_iva: false,
    is_default: true,
    sort_order: "",
  };
  const [form, setForm] = useState(emptyForm);
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    const search = filterModel.quickFilterValues?.join(" ") || "";
    try {
      const res = await getIndividualItemTypes({
        perPage: paginationModel.pageSize,
        page: paginationModel.page + 1,
        search,
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("Error al cargar.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      key: r.key,
      category: r.category,
      default_amount_mode: r.default_amount_mode,
      allows_interest: !!r.allows_interest,
      applies_iva: !!r.applies_iva,
      is_default: !!r.is_default,
      sort_order: r.sort_order ?? "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      showToast("El nombre es requerido.", "error");
      return;
    }
    if (!form.key) {
      showToast("No se pudo generar una clave válida desde el nombre.", "error");
      return;
    }
    setSaving(true);
    try {
      const p = { ...form, sort_order: form.sort_order !== "" ? parseInt(form.sort_order, 10) : null };
      if (editing) await updateIndividualItemType(editing.id, p);
      else await createIndividualItemType(p);
      setDialogOpen(false);
      load();
      showToast(editing ? "Actualizado." : "Creado.", "success");
    } catch (e) {
      showToast(e?.response?.data?.message ?? "Error al guardar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteIndividualItemType(deleteTarget.id);
      setDeleteTarget(null);
      load();
      showToast("Eliminado.", "success");
    } catch {
      showToast("Error al eliminar.", "error");
    }
  };

  const columns = [
    { field: "name", headerName: "Nombre", flex: 1.5 },
    { field: "category", headerName: "Categoría", width: 120, renderCell: (p) => p.value },
    {
      field: "default_amount_mode",
      headerName: "Monto por defecto",
      flex: 1,
      renderCell: (p) => (p.value === "jus_multiplier" ? "JUS (multiplicador)" : "Monto fijo"),
    },
    { field: "allows_interest", headerName: "Interés", width: 90, renderCell: (p) => (p.value ? "Sí" : "—") },
    { field: "applies_iva", headerName: "IVA", width: 80, renderCell: (p) => (p.value ? "Sí" : "—") },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteTarget(p.row)}>
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <DataGridCustom
        rows={rows}
        columns={columns}
        icon={<CategoryRoundedIcon />}
        title="Tipos de Rubros"
        description="Rubros disponibles para las liquidaciones comunes (separados de los de municipalidad)"
        loading={loading}
        total={total}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        filterModel={filterModel}
        onFilterChange={(m) => {
          setFilterModel(m);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        filterMode="server"
        isServerPagination
        pageSizeOptions={[5, 10, 25, 50]}
        headerActions={
          <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ borderRadius: 2.5, fontWeight: 900 }}>
            Agregar
          </Button>
        }
      />

      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar rubro" : "Nuevo rubro"}
        maxWidth="sm"
        fullWidth
        stickyHeader
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Nombre"
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setF("name", name);
                  if (!editing) {
                    const key = name
                      .toLowerCase()
                      .normalize("NFD")
                      .replace(/[̀-ͯ]/g, "")
                      .replace(/[^a-z0-9]+/g, "_")
                      .replace(/^_+|_+$/g, "");
                    setF("key", key ? `ind_${key}` : "");
                  }
                }}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="Orden" value={form.sort_order} onChange={(e) => setF("sort_order", e.target.value)} size="small" type="number" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoría</InputLabel>
                <Select value={form.category} label="Categoría" onChange={(e) => setF("category", e.target.value)}>
                  {CATEGORY_OPTIONS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Monto por defecto</InputLabel>
                <Select value={form.default_amount_mode} label="Monto por defecto" onChange={(e) => setF("default_amount_mode", e.target.value)}>
                  <MenuItem value="fixed">Monto fijo (pesos)</MenuItem>
                  <MenuItem value="jus_multiplier">JUS (multiplicador)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel control={<Switch checked={form.allows_interest} onChange={(e) => setF("allows_interest", e.target.checked)} />} label="Admite interés" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel control={<Switch checked={form.applies_iva} onChange={(e) => setF("applies_iva", e.target.checked)} />} label="Aplica IVA" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel control={<Switch checked={form.is_default} onChange={(e) => setF("is_default", e.target.checked)} />} label="Por defecto" />
            </Grid>
          </Grid>
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar rubro"
        description={`¿Eliminar el rubro "${deleteTarget?.name}"?`}
        confirmText="Eliminar"
        confirmColor="error"
      />
    </>
  );
}

// ── Juzgados ─────────────────────────────────────────────────────────────────

function DependenciesPanel() {
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [selected, setSelected] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const search = filterModel.quickFilterValues?.join(" ") || "";
      const res = await getIndividualDependenciesConfig({
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...(search && { search }),
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("Error al cargar juzgados.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    { field: "name", headerName: "Juzgado", flex: 2 },
    {
      field: "individual_liquidation_item_types",
      headerName: "Rubros configurados",
      flex: 1.5,
      sortable: false,
      renderCell: (p) => {
        const count = Array.isArray(p.row.individual_liquidation_item_types) ? p.row.individual_liquidation_item_types.length : 0;
        return count > 0 ? (
          <Typography variant="caption" sx={{ color: "success.main", fontWeight: 700 }}>
            {count} rubro{count !== 1 ? "s" : ""}
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ opacity: 0.5 }}>
            Por defecto
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <IconButton
          size="small"
          color="primary"
          onClick={() => {
            setSelected(p.row);
            setConfigOpen(true);
          }}
        >
          <TuneRoundedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <DataGridCustom
        rows={rows}
        columns={columns}
        icon={<GavelRoundedIcon />}
        title="Configuración por Juzgado"
        description="Rubros y multiplicadores JUS específicos para cada juzgado (no se comparten con municipalidad)"
        loading={loading}
        total={total}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        filterModel={filterModel}
        onFilterChange={(m) => {
          setFilterModel(m);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        filterMode="server"
        isServerPagination
        pageSizeOptions={[10, 15, 25, 50]}
        headerActions={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ borderRadius: 2.5, fontWeight: 900 }}
          >
            Agregar
          </Button>
        }
      />

      <CreateDependencyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={createIndividualDependency}
        onCreated={() => {
          load();
          showToast("Juzgado creado.", "success");
        }}
      />

      <IndividualDependencyItemTypesDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        dependency={selected}
        onSaved={() => {
          load();
          showToast("Configuración guardada.", "success");
        }}
      />
    </>
  );
}

// ── Tasas de interés ─────────────────────────────────────────────────────────

function InterestRatesPanel() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canImport = hasPermission("tasas interes judiciales");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [rateType, setRateType] = useState("pasiva");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ rate_type: "pasiva", date: null, accumulated_index: "", daily_rate: "", notes: "" });
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const [bcraLoading, setBcraLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getJudicialInterestRates({
        perPage: paginationModel.pageSize,
        page: paginationModel.page + 1,
        rate_type: rateType,
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("Error al cargar tasas.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, rateType, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ rate_type: rateType, date: null, accumulated_index: "", daily_rate: "", notes: "" });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      rate_type: r.rate_type,
      date: r.date ? dayjs(r.date) : null,
      accumulated_index: r.accumulated_index ?? "",
      daily_rate: r.daily_rate ?? "",
      notes: r.notes ?? "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const bancor = form.rate_type === "banco_cordoba";
    if (!form.date || (bancor ? form.daily_rate === "" : form.accumulated_index === "")) {
      showToast(bancor ? "Completá la fecha y la TNA anual." : "Completá la fecha y el índice acumulado.", "error");
      return;
    }
    setSaving(true);
    const payload = {
      rate_type: form.rate_type,
      date: form.date.format("YYYY-MM-DD"),
      accumulated_index: bancor ? null : parseFloat(form.accumulated_index),
      daily_rate: form.daily_rate !== "" ? parseFloat(form.daily_rate) : null,
      notes: form.notes || null,
    };
    try {
      if (editing) await updateJudicialInterestRate(editing.id, payload);
      else await createJudicialInterestRate(payload);
      setDialogOpen(false);
      load();
      showToast(editing ? "Actualizado." : "Creado.", "success");
    } catch (e) {
      showToast(e?.response?.data?.message ?? "Error al guardar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteJudicialInterestRate(deleteTarget.id);
      setDeleteTarget(null);
      load();
      showToast("Eliminado.", "success");
    } catch {
      showToast("Error al eliminar.", "error");
    }
  };

  const handleImportBcra = async () => {
    setBcraLoading(true);
    try {
      await importJudicialInterestRatesBcra({ rate_type: rateType });
      showToast("Importación desde el BCRA encolada. Se te notificará al finalizar.", "success");
    } catch (e) {
      showToast(e?.response?.data?.message ?? "No se pudo encolar la importación.", "error");
    } finally {
      setBcraLoading(false);
    }
  };

  const isBancor = rateType === "banco_cordoba";

  const columns = [
    { field: "date", headerName: isBancor ? "Vigencia desde" : "Fecha", flex: 1, renderCell: (p) => fmtDate(p.value) },
    {
      field: isBancor ? "daily_rate" : "accumulated_index",
      headerName: isBancor ? "TNA anual (%)" : "Índice acumulado (%)",
      flex: 1.2,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value }) => (value != null ? Number(value).toLocaleString("es-AR", { maximumFractionDigits: 4 }) : "—"),
    },
    { field: "source", headerName: "Origen", width: 100 },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => setDeleteTarget(p.row)}>
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Serie</InputLabel>
          <Select
            value={rateType}
            label="Serie"
            onChange={(e) => {
              setRateType(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
          >
            <MenuItem value="pasiva">{RATE_TYPE_LABELS.pasiva}</MenuItem>
            <MenuItem value="tim">{RATE_TYPE_LABELS.tim}</MenuItem>
            <MenuItem value="banco_cordoba">{RATE_TYPE_LABELS.banco_cordoba}</MenuItem>
          </Select>
        </FormControl>
        {canImport && rateType !== "banco_cordoba" && (
          <Button size="small" variant="outlined" startIcon={bcraLoading ? <CircularProgress size={14} /> : <CloudDownloadRoundedIcon />} onClick={handleImportBcra} disabled={bcraLoading} sx={{ borderRadius: 2 }}>
            Traer del BCRA
          </Button>
        )}
        {rateType === "banco_cordoba" && (
          <Typography sx={{ fontSize: 12, opacity: 0.6 }}>Sin importación automática — se carga manual.</Typography>
        )}
      </Box>

      <DataGridCustom
        rows={rows}
        columns={columns}
        icon={<PercentRoundedIcon />}
        title={`Tasas de interés — ${RATE_TYPE_LABELS[rateType]}`}
        description="Índice/coeficiente acumulado diario usado para calcular el interés de cada rubro"
        loading={loading}
        total={total}
        paginationModel={paginationModel}
        onPaginationChange={setPaginationModel}
        isServerPagination
        pageSizeOptions={[10, 25, 50, 100]}
        headerActions={
          <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ borderRadius: 2.5, fontWeight: 900 }}>
            Agregar
          </Button>
        }
      />

      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar tasa" : "Nueva tasa"}
        maxWidth="xs"
        fullWidth
        stickyHeader
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Serie</InputLabel>
            <Select value={form.rate_type} label="Serie" onChange={(e) => setF("rate_type", e.target.value)}>
              <MenuItem value="pasiva">{RATE_TYPE_LABELS.pasiva}</MenuItem>
              <MenuItem value="tim">{RATE_TYPE_LABELS.tim}</MenuItem>
              <MenuItem value="banco_cordoba">{RATE_TYPE_LABELS.banco_cordoba}</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            label={form.rate_type === "banco_cordoba" ? "Vigente desde" : "Fecha"}
            value={form.date}
            onChange={(v) => setF("date", v)}
            format="DD/MM/YYYY"
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          {form.rate_type === "banco_cordoba" ? (
            <TextField
              label="TNA anual (%)"
              value={form.daily_rate}
              onChange={(e) => setF("daily_rate", e.target.value)}
              size="small"
              type="number"
              helperText="Tasa Nominal Anual de pizarra de Bancor. Se aplica como base × TNA% × días/365."
            />
          ) : (
            <>
              <TextField label="Índice acumulado (%)" value={form.accumulated_index} onChange={(e) => setF("accumulated_index", e.target.value)} size="small" type="number" />
              <TextField label="Tasa diaria (opcional)" value={form.daily_rate} onChange={(e) => setF("daily_rate", e.target.value)} size="small" type="number" />
            </>
          )}
          <TextField label="Notas (opcional)" value={form.notes} onChange={(e) => setF("notes", e.target.value)} size="small" multiline rows={2} />
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar tasa"
        description={`¿Eliminar la tasa del ${fmtDate(deleteTarget?.date)}?`}
        confirmText="Eliminar"
        confirmColor="error"
      />
    </>
  );
}

// ── Página ───────────────────────────────────────────────────────────────────

export default function SettingsIndividualLiquidation() {
  const [tab, setTab] = useState("1");

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <ActionHeaderCard
        icon={<CalculateRoundedIcon />}
        title="Configuración de Liquidaciones — Comunes"
        description="Catálogos y parámetros del módulo de liquidaciones comunes."
        sx={{ mb: 2 }}
      />

      <GlassCard sx={{ p: 0 }}>
        <TabContext value={tab}>
          <TabList
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              px: 0.5,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { minHeight: 50, textTransform: "none", fontWeight: 900, px: 1.2 },
              "& .MuiTabs-flexContainer": { gap: 0.5 },
              "& .MuiTabs-indicator": { height: 3, borderRadius: 999 },
            }}
          >
            <Tab label="TIPOS DE RUBROS" value="1" />
            <Tab label="JUZGADOS" value="2" />
            <Tab label="CAJA DE ABOGADOS" value="4" />
            <Tab label="COLEGIO DE ABOGADOS" value="5" />
            <Tab label="TASAS DE INTERÉS" value="3" />
          </TabList>

          <TabPanel value="1" sx={{ p: 3 }}>
            <ItemTypesPanel />
          </TabPanel>
          <TabPanel value="2" sx={{ p: 3 }}>
            <DependenciesPanel />
          </TabPanel>
          <TabPanel value="3" sx={{ p: 3 }}>
            <InterestRatesPanel />
          </TabPanel>
          <TabPanel value="4" sx={{ p: 3 }}>
            <DateAmountPanel
              icon={<CategoryRoundedIcon />}
              title="Caja de Abogados (común)"
              subtitle="Monto propio del módulo común — no usa el valor de municipalidad"
              fetchFn={getIndividualBarAssocValues}
              createFn={createIndividualBarAssocValue}
              updateFn={updateIndividualBarAssocValue}
              deleteFn={deleteIndividualBarAssocValue}
              amountLabel="Cuota Caja ($)"
            />
          </TabPanel>
          <TabPanel value="5" sx={{ p: 3 }}>
            <DateAmountPanel
              icon={<GavelRoundedIcon />}
              title="Colegio de Abogados (común)"
              subtitle="Monto propio del módulo común — no usa el valor de municipalidad"
              fetchFn={getIndividualBarCollegeFees}
              createFn={createIndividualBarCollegeFee}
              updateFn={updateIndividualBarCollegeFee}
              deleteFn={deleteIndividualBarCollegeFee}
              amountLabel="Cuota Colegio ($)"
            />
          </TabPanel>
        </TabContext>
      </GlassCard>
    </Container>
  );
}
